import {useCallback, useEffect, useRef, useState} from 'react';
import {isSlideId, SlideId, SLIDES} from './slides';
import {MIDIAS_DOS_SLIDES} from './slides/dados';
import {precarregarMidias, urlDaMidia} from './midia';

/**
 * Tela de descanso do totem: passa propagandas em loop.
 *
 * A playlist fica em public/totem/playlist.json e pode misturar:
 *  - slides prontos (montados em HTML com o visual do site): { "tipo": "slide", "id": "planos" }
 *  - imagens:  { "tipo": "imagem", "url": "...", "url_vertical": "...", "duracao": 10 }
 *  - vídeos:   { "tipo": "video",  "url": "...", "url_vertical": "...", "som": false }
 * Se o arquivo faltar ou estiver vazio, roda a PLAYLIST_PADRAO abaixo.
 */

type ItemImagem = { tipo: 'imagem'; url: string; url_vertical?: string; duracao?: number };
type ItemVideo = { tipo: 'video'; url: string; url_vertical?: string; som?: boolean };
type ItemSlide = { tipo: 'slide'; id: SlideId; duracao?: number };
type ItemPlaylist = ItemImagem | ItemVideo | ItemSlide;

type Playlist = {
	itens: ItemPlaylist[];
}

interface TotemDescansoProps {
	onTocar: () => void;
	/** Chamado quando a equipe segura o canto superior direito por alguns segundos (bloqueia o totem). */
	onBloquear: () => void;
}

const DURACAO_IMAGEM_PADRAO = 10;
const SEGURAR_PARA_BLOQUEAR_MS = 3000;
const INTERVALO_RECARREGAR_PLAYLIST = 5 * 60 * 1000;

const PLAYLIST_PADRAO: ItemPlaylist[] = [
	{tipo: 'slide', id: 'boas-vindas'},
	{tipo: 'imagem', url: '/totem/midia/banner_1.jpg', url_vertical: '/totem/midia/banner_1_vertical.jpg', duracao: 10},
	{tipo: 'slide', id: 'planos'},
	{tipo: 'imagem', url: '/totem/midia/banner_3.jpg', url_vertical: '/totem/midia/banner_3_vertical.jpg', duracao: 10},
	{tipo: 'slide', id: 'streaming'},
	{tipo: 'imagem', url: '/totem/midia/banner_2.jpg', url_vertical: '/totem/midia/banner_2_vertical.jpg', duracao: 10},
	{tipo: 'slide', id: 'app'},
];

function itemValido(i: any): i is ItemPlaylist {
	if (!i || typeof i !== 'object') return false;
	if (i.tipo === 'slide') return isSlideId(i.id);
	if (i.tipo === 'imagem' || i.tipo === 'video') return typeof i.url === 'string' && i.url !== '';
	return false;
}

// baixa as imagens da playlist (e as dos slides) uma vez só, para a memória: a primeira volta do loop
// não mostra tela vazia e as seguintes não dependem da rede
function precarregarImagens(lista: ItemPlaylist[]) {
	const urls: Array<string | undefined> = [...MIDIAS_DOS_SLIDES];
	lista.forEach(item => {
		if (item.tipo === 'imagem') {
			urls.push(item.url, item.url_vertical);
		}
	});
	precarregarMidias(urls);
}

function chaveDoItem(item: ItemPlaylist) {
	return item.tipo === 'slide' ? `slide:${item.id}` : `${item.tipo}:${item.url}`;
}

function useVertical() {
	const [vertical, setVertical] = useState(window.innerHeight > window.innerWidth);
	useEffect(() => {
		const atualizar = () => setVertical(window.innerHeight > window.innerWidth);
		window.addEventListener('resize', atualizar);
		return () => window.removeEventListener('resize', atualizar);
	}, []);
	return vertical;
}

export function TotemDescanso({onTocar, onBloquear}: TotemDescansoProps) {
	const [itensPlaylist, setItensPlaylist] = useState<ItemPlaylist[]>(PLAYLIST_PADRAO);
	const [chavesComErro, setChavesComErro] = useState<string[]>([]);
	const [indice, setIndice] = useState(0);
	const timerRef = useRef<number | null>(null);
	const vertical = useVertical();

	// canto superior direito: segurar o dedo bloqueia o totem (volta para a tela da senha); toque curto age como o resto da tela
	const travaTimerRef = useRef<number | null>(null);
	const travouRef = useRef(false);

	const cancelarTrava = useCallback(() => {
		if (travaTimerRef.current) {
			window.clearTimeout(travaTimerRef.current);
			travaTimerRef.current = null;
		}
	}, []);

	const iniciarTrava = useCallback(() => {
		cancelarTrava();
		travouRef.current = false;
		travaTimerRef.current = window.setTimeout(() => {
			travaTimerRef.current = null;
			travouRef.current = true;
			onBloquear();
		}, SEGURAR_PARA_BLOQUEAR_MS);
	}, [cancelarTrava, onBloquear]);

	useEffect(() => cancelarTrava, [cancelarTrava]);

	// itens cujo arquivo falhou ao carregar saem do loop até a próxima recarga da playlist
	const itens = itensPlaylist.filter(i => chavesComErro.indexOf(chaveDoItem(i)) === -1);

	const carregarPlaylist = useCallback(() => {
		fetch('/totem/playlist.json?t=' + Date.now(), {cache: 'no-store'})
			.then(r => r.ok ? r.json() : Promise.reject(r.status))
			.then((playlist: Playlist) => {
				const validos = (playlist?.itens || []).filter(itemValido);
				const lista = validos.length > 0 ? validos : PLAYLIST_PADRAO;
				precarregarImagens(lista);
				setItensPlaylist(lista);
				setChavesComErro([]);
				setIndice(0);
			})
			.catch(() => {
				precarregarImagens(PLAYLIST_PADRAO);
				setItensPlaylist(PLAYLIST_PADRAO);
			});
	}, []);

	useEffect(() => {
		carregarPlaylist();
		const intervalo = window.setInterval(carregarPlaylist, INTERVALO_RECARREGAR_PLAYLIST);
		return () => window.clearInterval(intervalo);
	}, [carregarPlaylist]);

	const proximo = useCallback(() => {
		setIndice(atual => (itens.length === 0 ? 0 : (atual + 1) % itens.length));
	}, [itens.length]);

	const item = itens.length > 0 ? itens[indice % itens.length] : null;

	const marcarErro = useCallback(() => {
		if (item) {
			const chave = chaveDoItem(item);
			setChavesComErro(atual => (atual.indexOf(chave) === -1 ? [...atual, chave] : atual));
		}
	}, [item]);

	useEffect(() => {
		if (timerRef.current) {
			window.clearTimeout(timerRef.current);
			timerRef.current = null;
		}
		if (item && item.tipo !== 'video') {
			let duracao = DURACAO_IMAGEM_PADRAO;
			if (item.tipo === 'slide') {
				duracao = item.duracao || SLIDES[item.id].duracao;
			} else if (item.duracao) {
				duracao = item.duracao;
			}
			timerRef.current = window.setTimeout(proximo, duracao * 1000);
		}
		return () => {
			if (timerRef.current) {
				window.clearTimeout(timerRef.current);
			}
		};
	}, [item, proximo]);

	const urlDe = (i: ItemImagem | ItemVideo) => {
		const url = vertical && i.url_vertical ? i.url_vertical : i.url;
		return i.tipo === 'imagem' ? urlDaMidia(url) : url; // vídeo fica fora do cache em memória (pode ser grande)
	};
	const chave = item ? `${chaveDoItem(item)}#${indice}` : 'vazio';

	let conteudo = null;
	if (item && item.tipo === 'imagem') {
		// a mesma imagem desfocada preenche as bordas quando a proporção do banner não bate com a da tela
		conteudo = (
			<div key={chave} className="totem-midia-moldura">
				<img src={urlDe(item)} alt="" className="totem-midia totem-midia-fundo" aria-hidden="true"/>
				<img src={urlDe(item)} alt="" className="totem-midia totem-midia-imagem" onError={marcarErro}/>
			</div>
		);
	} else if (item && item.tipo === 'video') {
		conteudo = (
			<video
				key={chave}
				src={urlDe(item)}
				className="totem-midia"
				autoPlay
				muted={!item.som}
				playsInline
				onEnded={proximo}
				onError={marcarErro}
			/>
		);
	} else if (item && item.tipo === 'slide') {
		const Slide = SLIDES[item.id].componente;
		conteudo = <div key={chave} className="totem-slide"><Slide vertical={vertical}/></div>;
	}

	return (
		<div className="totem-descanso" onClick={onTocar}>
			{conteudo}
			<div
				className="totem-descanso-trava"
				aria-hidden="true"
				onPointerDown={iniciarTrava}
				onPointerUp={cancelarTrava}
				onPointerLeave={cancelarTrava}
				onPointerCancel={cancelarTrava}
				onContextMenu={e => e.preventDefault()}
				onClick={e => {
					e.stopPropagation();
					if (!travouRef.current) {
						onTocar();
					}
				}}
			/>
			<div className="totem-descanso-chamada">
				<span className="material-icons" aria-hidden="true">touch_app</span>
				Toque na tela para consultar seus boletos
			</div>
		</div>
	);
}
